import type {
  BacklogChildRow,
  FinancialWorkFeed,
  FinancialWorkFeedItem,
  LiffFinancialWorkCard,
  WorkQueueProjection,
} from "./types";
import {
  cardBatchClosed,
  cardBatchId,
  cardBatchSize,
  clusterCardsByBatch,
  displayWorkTitle,
  isBacklogChildPaid,
  isClosedWorkCard,
  isFromBatchCard,
  isReceivableBatchCard,
  isSharedBatchCard,
  isSlipOwnerAskCard,
  normalizeBatchId,
} from "./nav";
import { HUMAN_ASK_KINDS, type HumanAskKind } from "./types";
import {
  BILLIN_DEFAULT_WORKER_ID,
  BILLIN_RECEIVABLE_SLIP_WORKER_ID,
  BILLIN_RECEIVABLE_WORKER_ID,
} from "@/lib/billinConstants";
import { EXPENSE_TRANSACTION_WORKER_ID } from "@/lib/transactionSummary";
import { BILLIN_SCROLL_TO_RECEIVABLE, BILLIN_SCROLL_TO_TRANSACTIONS } from "@/lib/billinGoRoutes";
import { translateReasonSubtitle } from "./reasonLabelsTh";
import type { QueueErrorKind } from "./queueClient";
import { closedFeedCardHints, feedItemProvenanceSourceChannel } from "./incomeRecordQueueDisplay";

export const FEED_PAGE_SIZE = 20;

export const FEED_CONTEXT_KEY = "feed";

const LIST_BUCKETS = ["needs_action", "waiting", "recently_closed"] as const;

export const FEED_TAB_SLUGS = {
  needs_action: "needs-action",
  waiting: "waiting",
  recently_closed: "recently-closed",
  all: "all",
} as const;

export type FeedTabId = keyof typeof FEED_TAB_SLUGS;

export function parseFeedTabSlug(raw: string | null | undefined): FeedTabId | null {
  const key = (raw || "").trim().toLowerCase().replace(/_/g, "-");
  if (key === "needs-action") return "needs_action";
  if (key === "waiting") return "waiting";
  if (key === "recently-closed") return "recently_closed";
  if (key === "all") return "all";
  return null;
}

export function feedTabSlug(bucket: string | null | undefined): string {
  const parsed = parseFeedTabSlug(bucket) ?? "needs_action";
  return FEED_TAB_SLUGS[parsed];
}

export type FeedFetchOutcome =
  | { kind: "ok"; feed: FinancialWorkFeed }
  | { kind: "missing" }
  | { kind: "unauthorized"; message: string }
  | { kind: "error"; message: string };

function readErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const rec = body as { detail?: unknown; error?: unknown };
  if (typeof rec.detail === "string" && rec.detail.trim()) return rec.detail;
  if (typeof rec.error === "string" && rec.error.trim()) return rec.error;
  return fallback;
}

export function interpretFeedHttp(status: number, body: unknown): FeedFetchOutcome {
  if (status === 401 || status === 403) {
    return {
      kind: "unauthorized",
      message: readErrorMessage(body, "ไม่มีสิทธิ์ดูคิวงานเงินนี้"),
    };
  }
  if (status === 404 || status === 502 || status === 503) {
    return { kind: "missing" };
  }
  if (status < 200 || status >= 300) {
    return {
      kind: "error",
      message: readErrorMessage(body, `โหลดรายการเงินไม่สำเร็จ (${status})`),
    };
  }
  const feed = body as FinancialWorkFeed;
  if (!feed || !Array.isArray(feed.buckets)) {
    return { kind: "missing" };
  }
  return { kind: "ok", feed };
}

export function isFeedCard(card: LiffFinancialWorkCard | null | undefined): boolean {
  return Boolean(card?.contextRows?.some((row) => row.key === FEED_CONTEXT_KEY));
}

export function isClosedQueueCard(
  card: LiffFinancialWorkCard | null | undefined,
): boolean {
  if (!card) return false;
  return (
    card.workPlacement?.bucket === "recently_closed" ||
    card.surfaceState === "closed" ||
    Boolean(card.requirementStatus?.complete)
  );
}

export function queueRowShowsClosedStyle(
  filter: "all" | "needs_action" | "waiting" | "recently_closed",
  card: LiffFinancialWorkCard,
): boolean {
  return filter === "recently_closed" || isClosedQueueCard(card);
}

/**
 * Open rows on the Bill-Lin record drawer. BILL-Lin กำลังถือ and
 * ปิดล่าสุด stay on the work-detail drawer — same as ต้องให้คุณทำ for asks.
 */
export function feedCardOpensRecordSurface(
  card: LiffFinancialWorkCard | null | undefined,
): boolean {
  if (!card || !isFeedCard(card) || isSlipOwnerAskCard(card)) return false;
  if (
    card.workPlacement?.bucket === "waiting" ||
    card.surfaceState === "waiting"
  ) {
    return false;
  }
  if (isClosedQueueCard(card)) {
    return false;
  }
  return Boolean(
    card.availableActions.find((action) => action.actionType === "open_data")
      ?.dataNav?.workerId,
  );
}

function dataNavForItem(item: FinancialWorkFeedItem) {
  if (item.kind === "expense") {
    return {
      workerId: EXPENSE_TRANSACTION_WORKER_ID,
      recordId: item.objectRef.id,
      scrollTo: BILLIN_SCROLL_TO_TRANSACTIONS,
      highlight: item.objectRef.id,
    };
  }
  if (item.kind === "receivable") {
    return {
      workerId: BILLIN_RECEIVABLE_WORKER_ID,
      recordId: item.objectRef.id,
      scrollTo: BILLIN_SCROLL_TO_RECEIVABLE,
      highlight: item.objectRef.id,
    };
  }
  const closed =
    item.bucket === "recently_closed" || item.status === "fully_allocated";
  const incomeRecordId = item.incomeRecordId?.trim() || "";
  const recordId =
    closed && incomeRecordId ? incomeRecordId : item.objectRef.id;
  return {
    workerId: closed
      ? BILLIN_DEFAULT_WORKER_ID
      : BILLIN_RECEIVABLE_SLIP_WORKER_ID,
    recordId,
    scrollTo: BILLIN_SCROLL_TO_TRANSACTIONS,
    highlight: recordId,
  };
}

export function isJunkUnmatchedFeedItem(item: FinancialWorkFeedItem): boolean {
  if (item.askKind === "unmatched_slip" && (item.amountMinor ?? 0) <= 0) {
    return true;
  }
  return /(?:^|[^\d,])0\s*บาท\s*ยังจับคู่ไม่ได้/.test(item.title || "");
}

function feedItemQueueAt(item: FinancialWorkFeedItem): string {
  return item.createdAt?.trim() || item.updatedAt?.trim() || "";
}

export function cardQueueUpdatedAt(card: LiffFinancialWorkCard): string {
  const created = card.contextRows
    ?.find((row) => row.key === "created_at")
    ?.value?.trim();
  if (created) return created;
  const occurred = card.timelineSummary?.occurredAt?.trim();
  if (occurred) return occurred;
  return (
    card.contextRows?.find((row) => row.key === "updated_at")?.value?.trim() ||
    ""
  );
}

function queueAtMs(value: string): number {
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
}

/** Newest `created_at` first — matches server queue ordering. */
export function sortWorkQueueCards(
  cards: LiffFinancialWorkCard[],
): LiffFinancialWorkCard[] {
  return [...cards].sort(
    (a, b) =>
      queueAtMs(cardQueueUpdatedAt(b)) - queueAtMs(cardQueueUpdatedAt(a)),
  );
}

function feedItemBacklogStatus(
  item: FinancialWorkFeedItem,
): BacklogChildRow["status"] {
  if (
    item.bucket === "recently_closed" ||
    item.status === "closed" ||
    item.status === "fully_allocated"
  ) {
    return "paid";
  }
  const paid = item.amountPaidMinor ?? 0;
  const remaining = item.remainingAmountMinor ?? item.amountMinor;
  if (paid > 0 && remaining > 0) return "waiting_slip";
  return "open";
}

function feedItemCustomerLabel(item: FinancialWorkFeedItem): string {
  const fromTitle = item.title
    .replace(/\s+(ค้าง|จ่าย)\s+[\d,.]+\s*บาท$/, "")
    .trim();
  if (item.kind === "receivable" && fromTitle) return fromTitle;
  const fromSource = item.sourceLabel?.replace(/^ยอดค้าง ·\s*/, "").trim();
  if (fromSource && fromSource !== "ยอดค้าง") return fromSource;
  return fromTitle || item.title;
}

function feedItemToBacklogChild(item: FinancialWorkFeedItem): BacklogChildRow {
  const remaining =
    item.remainingAmountMinor != null && item.remainingAmountMinor >= 0
      ? item.remainingAmountMinor
      : item.amountMinor;
  const paid = item.amountPaidMinor ?? 0;
  return {
    objectRef: item.objectRef,
    label: feedItemCustomerLabel(item),
    status: feedItemBacklogStatus(item),
    remainingAmountMinor: remaining,
    paidAmountMinor: paid > 0 ? paid : undefined,
    originalAmountMinor: item.amountMinor,
    occurredAt: feedItemQueueAt(item),
    expiresAt: item.expiresAt?.trim() || undefined,
    currency: "THB",
  };
}

function normalizeBacklogChild(row: BacklogChildRow): BacklogChildRow {
  if (isBacklogChildPaid(row)) {
    return row.status === "paid" ? row : { ...row, status: "paid" };
  }
  return row;
}

function normalizeBacklogChildren(
  rows: BacklogChildRow[] | undefined,
): BacklogChildRow[] | undefined {
  if (!rows?.length) return rows;
  return rows.map(normalizeBacklogChild);
}

function rosterScore(roster: BacklogChildRow[] | undefined): number {
  if (!roster?.length) return 0;
  const paid = roster.filter((row) => isBacklogChildPaid(row)).length;
  return roster.length * 100 + paid;
}

function bestBatchRoster(
  left: BacklogChildRow[] | undefined,
  right: BacklogChildRow[] | undefined,
): BacklogChildRow[] | undefined {
  if (rosterScore(right) > rosterScore(left)) return right;
  return left;
}

function rosterNeedsUpgrade(
  card: LiffFinancialWorkCard,
  roster: BacklogChildRow[] | undefined,
): boolean {
  if (!roster?.length) return true;
  const expectedClosed = cardBatchClosed(card);
  const paid = roster.filter((row) => isBacklogChildPaid(row)).length;
  if (expectedClosed > paid) return true;
  return cardBatchSize(card) > roster.length;
}

function overlapSeedIds(card: LiffFinancialWorkCard): Set<string> {
  const ids = new Set(
    (card.backlogChildren ?? []).map((row) => row.objectRef.id),
  );
  if (card.objectRef.kind === "receivable") {
    ids.add(card.objectRef.id);
  }
  return ids;
}

function rostersOverlapCard(
  card: LiffFinancialWorkCard,
  roster: BacklogChildRow[] | undefined,
): boolean {
  if (!roster?.length) return false;
  const ids = overlapSeedIds(card);
  if (!ids.size) return false;
  return roster.some((row) => ids.has(row.objectRef.id));
}

function findOverlapRoster(
  card: LiffFinancialWorkCard,
  queue: WorkQueueProjection,
): BacklogChildRow[] | undefined {
  if (cardBatchSize(card) < 2 && overlapSeedIds(card).size < 2) return undefined;
  let best: BacklogChildRow[] | undefined;
  for (const bucket of queue.buckets) {
    for (const queueCard of bucket.cards) {
      const roster = normalizeBacklogChildren(queueCard.backlogChildren);
      if ((roster?.length ?? 0) < 2) continue;
      if (!rostersOverlapCard(card, roster)) continue;
      best = bestBatchRoster(best, roster);
    }
  }
  return best;
}

function queueRosterForCard(
  card: LiffFinancialWorkCard,
  queue: WorkQueueProjection,
): BacklogChildRow[] | undefined {
  const rosterByBatchId = new Map<string, BacklogChildRow[]>();
  for (const bucket of queue.buckets) {
    for (const queueCard of bucket.cards) {
      const roster = normalizeBacklogChildren(queueCard.backlogChildren);
      if ((roster?.length ?? 0) < 2) continue;
      indexBatchRoster(rosterByBatchId, queueCard, roster!);
    }
  }
  const fromBatchId = lookupBatchRoster(rosterByBatchId, cardBatchId(card));
  return bestBatchRoster(fromBatchId, findOverlapRoster(card, queue));
}

function batchLookupKeys(card: LiffFinancialWorkCard): string[] {
  const keys = new Set<string>();
  const fromRow = normalizeBatchId(cardBatchId(card));
  if (fromRow) keys.add(fromRow);
  if (card.objectRef.kind === "receivable_batch") {
    keys.add(card.objectRef.id);
    const fromObject = normalizeBatchId(card.objectRef.id);
    if (fromObject) keys.add(fromObject);
  }
  return [...keys].filter(Boolean);
}

function lookupBatchRoster(
  map: Map<string, BacklogChildRow[]>,
  batchId: string | null | undefined,
): BacklogChildRow[] | undefined {
  const norm = normalizeBatchId(batchId);
  if (!norm) return undefined;
  return map.get(norm) ?? map.get(`batch:${norm}`);
}

function indexBatchRoster(
  map: Map<string, BacklogChildRow[]>,
  card: LiffFinancialWorkCard,
  roster: BacklogChildRow[],
): void {
  for (const key of batchLookupKeys(card)) {
    map.set(key, bestBatchRoster(map.get(key), roster)!);
  }
}

/** Merge full batch rosters from work-queue projection into feed cards. */
export function enrichFeedCardsWithQueueRoster(
  projection: WorkQueueProjection,
  queue: WorkQueueProjection | null | undefined,
): WorkQueueProjection {
  if (!queue?.buckets?.length) return projection;

  const rosterByBatchId = new Map<string, BacklogChildRow[]>();
  const rosterByObjectKey = new Map<string, BacklogChildRow[]>();

  for (const bucket of queue.buckets) {
    for (const card of bucket.cards) {
      const roster = normalizeBacklogChildren(card.backlogChildren);
      if ((roster?.length ?? 0) < 2) continue;
      rosterByObjectKey.set(
        `${card.objectRef.kind}:${card.objectRef.id}`,
        roster!,
      );
      indexBatchRoster(rosterByBatchId, card, roster!);
    }
  }

  if (rosterByBatchId.size === 0 && rosterByObjectKey.size === 0) {
    return projection;
  }

  const enrichCard = (card: LiffFinancialWorkCard): LiffFinancialWorkCard => {
    let roster = normalizeBacklogChildren(card.backlogChildren);
    const fromBatch = lookupBatchRoster(rosterByBatchId, cardBatchId(card));
    const fromOverlap = findOverlapRoster(card, queue);
    const fromQueue = bestBatchRoster(fromBatch, fromOverlap);
    if (fromQueue && rosterNeedsUpgrade(card, roster)) {
      roster = bestBatchRoster(roster, fromQueue);
    }
    const fromObject = rosterByObjectKey.get(
      `${card.objectRef.kind}:${card.objectRef.id}`,
    );
    if (fromObject && rosterNeedsUpgrade(card, roster)) {
      roster = bestBatchRoster(roster, fromObject);
    }
    if (!roster || roster === card.backlogChildren) return syncCardFromBatchRoster(card);
    return syncCardFromBatchRoster({ ...card, backlogChildren: roster });
  };

  return expandOpenBatchMatesInProjection({
    ...projection,
    buckets: projection.buckets.map((bucket) => ({
      ...bucket,
      cards: bucket.cards.map(enrichCard),
    })),
    pageCards: projection.pageCards?.map(enrichCard),
  });
}

function cardObjectKey(card: LiffFinancialWorkCard): string {
  return `${card.objectRef.kind}:${card.objectRef.id}`;
}

function isOpenReceivableQueueCard(card: LiffFinancialWorkCard): boolean {
  const kind = card.objectRef.kind;
  if (kind !== "receivable" && !isReceivableBatchCard(card)) return false;
  return !isClosedQueueCard(card);
}

/** Queue often keeps open mates as one `receivable_batch` card — flatten to people. */
function openReceivableCardsFromQueueCard(
  card: LiffFinancialWorkCard,
): LiffFinancialWorkCard[] {
  const asWaiting = (next: LiffFinancialWorkCard): LiffFinancialWorkCard => ({
    ...next,
    workPlacement: {
      ...(next.workPlacement ?? card.workPlacement ?? { rank: 1 }),
      bucket: "waiting",
    },
  });

  if (isReceivableBatchCard(card)) {
    const roster = rosterForAggregateCard(card);
    const openChildren = (roster ?? []).filter((child) => !isBacklogChildPaid(child));
    if (openChildren.length === 0) return [];
    const batchId = batchIdForAggregateCard(card);
    const fullRoster = roster ?? openChildren;
    return openChildren.map((child) =>
      asWaiting(
        ensureBatchContextRows(
          batchMateCardFromChild(card, child, fullRoster),
          batchId,
          fullRoster,
        ),
      ),
    );
  }

  if (!isOpenReceivableQueueCard(card)) return [];
  return [asWaiting(card)];
}

function collectOpenReceivableQueueCards(
  queue: WorkQueueProjection,
): LiffFinancialWorkCard[] {
  const out: LiffFinancialWorkCard[] = [];
  const seen = new Set<string>();
  for (const bucket of queue.buckets) {
    if (bucket.id === "recently_closed") continue;
    for (const card of bucket.cards) {
      for (const open of openReceivableCardsFromQueueCard(card)) {
        const key = cardObjectKey(open);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(open);
      }
    }
  }
  return out;
}

/**
 * ลูกค้ายังค้าง comes from the receivable queue. The money feed can omit
 * Redis-only / hot ยอดค้าง, which leaves every งานเงิน tab empty. Reattach
 * those open rows onto กำลังถือ so the list matches the outstanding total.
 *
 * Tab badges always use max(feed waiting, queue open) so switching tabs does
 * not change the กำลังถือ / ทั้งหมด numbers. Only the current page list grows
 * with missing mates while viewing กำลังถือ / ทั้งหมด.
 */
export function mergeOpenReceivableQueueCards(
  projection: WorkQueueProjection,
  queue: WorkQueueProjection | null | undefined,
): WorkQueueProjection {
  if (!queue?.buckets?.length) return projection;
  const queueOpen = collectOpenReceivableQueueCards(queue);
  if (queueOpen.length === 0) return projection;

  const pageBucket = projection.page?.bucket ?? "";
  const onWaitingPage = pageBucket === "waiting" || pageBucket === "all";
  const seen = new Set<string>();
  for (const card of projection.pageCards ?? []) seen.add(cardObjectKey(card));
  for (const bucket of projection.buckets) {
    for (const card of bucket.cards) seen.add(cardObjectKey(card));
  }
  const extras = queueOpen.filter((card) => !seen.has(cardObjectKey(card)));
  const feedWaiting = projection.tabCounts?.waiting ?? 0;
  // Badge counts must stay stable across tabs. Do not inflate waiting only while
  // viewing กำลังถือ — that made the badge flip (e.g. 2 → 1) on tab switch.
  // Prefer the fuller of feed vs data-tab open receivables.
  const waitingCount = Math.max(feedWaiting, queueOpen.length);
  if (waitingCount === feedWaiting && (!onWaitingPage || extras.length === 0)) {
    return projection;
  }

  const addedToPage = onWaitingPage ? extras : [];
  const buckets = projection.buckets.map((bucket) => {
    if (bucket.id !== "waiting") return bucket;
    return {
      ...bucket,
      cards: onWaitingPage
        ? [...bucket.cards, ...extras]
        : bucket.cards,
      count: waitingCount,
    };
  });
  const tabCounts = projection.tabCounts
    ? {
        ...projection.tabCounts,
        waiting: waitingCount,
        all: (projection.tabCounts.all ?? 0) - feedWaiting + waitingCount,
      }
    : projection.tabCounts;
  return {
    ...projection,
    buckets,
    tabCounts,
    pageCards: onWaitingPage
      ? [...(projection.pageCards ?? []), ...addedToPage]
      : projection.pageCards,
    page: projection.page
      ? {
          ...projection.page,
          total:
            pageBucket === "waiting"
              ? waitingCount
              : pageBucket === "all"
                ? (tabCounts?.all ?? projection.page.total)
                : projection.page.total,
        }
      : projection.page,
  };
}

function collectClosedQueueCards(
  queue: WorkQueueProjection,
): LiffFinancialWorkCard[] {
  const out: LiffFinancialWorkCard[] = [];
  const seen = new Set<string>();
  for (const bucket of queue.buckets) {
    if (bucket.id !== "recently_closed") continue;
    for (const card of bucket.cards) {
      if (!isClosedQueueCard(card)) continue;
      const key = cardObjectKey(card);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        ...card,
        workPlacement: {
          ...(card.workPlacement ?? { rank: 1 }),
          bucket: "recently_closed",
        },
      });
    }
  }
  return out;
}

/**
 * When the money feed still omits a just-matched slip (empty workspace_id /
 * stale cache), reattach closed rows from the receivable queue onto ปิดล่าสุด.
 * Badge uses max(feed, queue) so it stays stable across tabs.
 */
export function mergeClosedQueueCards(
  projection: WorkQueueProjection,
  queue: WorkQueueProjection | null | undefined,
): WorkQueueProjection {
  if (!queue?.buckets?.length) return projection;
  const queueClosed = collectClosedQueueCards(queue);
  if (queueClosed.length === 0) return projection;

  const feedClosed = projection.tabCounts?.recently_closed ?? 0;
  const closedCount = Math.max(feedClosed, queueClosed.length);
  const pageBucket = projection.page?.bucket ?? "";
  const onClosedPage = pageBucket === "recently_closed" || pageBucket === "all";
  const pageHasClosed = (projection.pageCards ?? []).some((card) =>
    isClosedQueueCard(card),
  );
  // Only inject queue closed cards when the feed page missed them entirely —
  // otherwise payment + receivable for the same match would double-list.
  const seen = new Set<string>();
  for (const card of projection.pageCards ?? []) seen.add(cardObjectKey(card));
  const extras =
    onClosedPage && !pageHasClosed && feedClosed === 0
      ? queueClosed.filter((card) => !seen.has(cardObjectKey(card)))
      : [];

  if (closedCount === feedClosed && extras.length === 0) {
    return projection;
  }

  const tabCounts = projection.tabCounts
    ? {
        ...projection.tabCounts,
        recently_closed: closedCount,
        all: (projection.tabCounts.all ?? 0) - feedClosed + closedCount,
      }
    : projection.tabCounts;
  return {
    ...projection,
    tabCounts,
    buckets: projection.buckets.map((bucket) => {
      if (bucket.id !== "recently_closed") return bucket;
      return {
        ...bucket,
        count: closedCount,
        cards: onClosedPage ? [...bucket.cards, ...extras] : bucket.cards,
      };
    }),
    pageCards: onClosedPage
      ? [...(projection.pageCards ?? []), ...extras]
      : projection.pageCards,
    page: projection.page
      ? {
          ...projection.page,
          total:
            pageBucket === "recently_closed"
              ? closedCount
              : pageBucket === "all"
                ? (tabCounts?.all ?? projection.page.total)
                : projection.page.total,
        }
      : projection.page,
  };
}

function batchMateTitle(label: string, remainingMinor: number): string {
  const major = remainingMinor / 100;
  const amount = major.toLocaleString("th-TH", {
    minimumFractionDigits: major % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${label} ค้าง ${amount} บาท`;
}

function rosterChildForCard(
  card: LiffFinancialWorkCard,
): BacklogChildRow | undefined {
  const roster = card.backlogChildren;
  if (!roster?.length) return undefined;
  return roster.find((row) => row.objectRef.id === card.objectRef.id);
}

function partialAmountsFromChild(child: BacklogChildRow): {
  paid: number;
  remaining: number | null;
  original: number | null;
} {
  const paid = child.paidAmountMinor ?? 0;
  const original = child.originalAmountMinor ?? null;
  const remaining =
    child.remainingAmountMinor ??
    (original != null && paid > 0 ? Math.max(original - paid, 0) : null);
  return { paid, remaining, original };
}

/** Apply live paid/remaining from batch roster onto the visible queue row. */
export function syncCardFromBatchRoster(
  card: LiffFinancialWorkCard,
): LiffFinancialWorkCard {
  if (card.objectRef.kind !== "receivable" || isClosedWorkCard(card)) return card;
  const child = rosterChildForCard(card);
  if (!child) return card;

  const { paid, remaining, original } = partialAmountsFromChild(child);
  const isPartial =
    child.status === "waiting_slip" ||
    (paid > 0 && remaining != null && remaining > 0);
  if (!isPartial) return card;

  const label = child.label || displayWorkTitle(card.title);
  const remainingMinor =
    remaining ??
    card.outstanding?.remainingAmountMinor ??
    card.amountSummary?.primaryAmountMinor ??
    0;
  const title = batchMateTitle(label, remainingMinor);
  const nextLabel =
    card.nextStep?.label?.includes("จับคู่") ||
    card.nextStep?.label === "รอสลิป"
      ? "จ่ายบางส่วน รอสลิปส่วนที่เหลือ"
      : card.nextStep?.label;

  return {
    ...card,
    title,
    amountSummary: {
      currency: child.currency ?? card.amountSummary?.currency ?? "THB",
      primaryAmountMinor: remainingMinor,
      basis: "remaining",
    },
    outstanding: {
      openCount: 1,
      remainingAmountMinor: remainingMinor,
      currency: child.currency ?? "THB",
      items: [
        {
          objectRef: card.objectRef,
          label,
          status: "waiting_slip",
          remainingAmountMinor: remainingMinor,
          paidAmountMinor: paid,
          originalAmountMinor:
            original ??
            card.outstanding?.items?.[0]?.originalAmountMinor ??
            remainingMinor + paid,
          occurredAt: child.occurredAt,
          expiresAt: child.expiresAt,
        },
      ],
    },
    nextStep: card.nextStep
      ? { ...card.nextStep, label: nextLabel ?? card.nextStep.label }
      : card.nextStep,
  };
}

function batchMateCardFromChild(
  template: LiffFinancialWorkCard,
  child: BacklogChildRow,
  roster: BacklogChildRow[],
): LiffFinancialWorkCard {
  const remainingMinor =
    child.remainingAmountMinor ??
    child.originalAmountMinor ??
    template.amountSummary?.primaryAmountMinor ??
    0;
  const occurredAt =
    child.occurredAt?.trim() ||
    cardQueueUpdatedAt(template) ||
    template.timelineSummary?.occurredAt?.trim() ||
    "";
  const title = batchMateTitle(child.label, remainingMinor);
  const contextRows = (template.contextRows ?? []).filter(
    (row) => row.key !== "ask_kind",
  );
  const mate: LiffFinancialWorkCard = {
    ...template,
    objectRef: child.objectRef,
    title,
    subtitle: `ยอดค้าง · ${child.label}`,
    contextRows,
    consequenceRows: [],
    amountSummary: {
      currency: child.currency ?? template.amountSummary?.currency ?? "THB",
      primaryAmountMinor: remainingMinor,
      basis: "remaining",
    },
    outstanding: {
      openCount: 1,
      remainingAmountMinor: remainingMinor,
      currency: child.currency ?? "THB",
      items: [
        {
          objectRef: child.objectRef,
          label: child.label,
          status:
            child.status === "waiting_slip" ? "waiting_slip" : "open",
          remainingAmountMinor: remainingMinor,
          paidAmountMinor: child.paidAmountMinor ?? 0,
          originalAmountMinor: child.originalAmountMinor ?? remainingMinor,
          occurredAt: child.occurredAt,
          expiresAt: child.expiresAt,
        },
      ],
    },
    requirementStatus: {
      complete: false,
      summary: template.requirementStatus.summary,
    },
    timelineSummary: occurredAt
      ? { latestEvent: title, occurredAt }
      : template.timelineSummary,
    timelineEvents: occurredAt
      ? [
          {
            id: `tl-batch-mate-${child.objectRef.id}`,
            label: `ยอดค้าง · ${child.label}`,
            occurredAtLabel: occurredAt,
          },
        ]
      : template.timelineEvents,
    backlogChildren: roster,
    availableActions: template.availableActions.map((action) => ({
      ...action,
      objectRef:
        action.objectRef?.kind === "receivable"
          ? child.objectRef
          : action.objectRef,
    })),
  };
  if (!occurredAt) return mate;
  const rows = [...(mate.contextRows ?? [])];
  const stampCreated = {
    key: "created_at",
    label: "เวลาสร้าง",
    value: occurredAt,
  };
  const createdIdx = rows.findIndex((row) => row.key === "created_at");
  if (createdIdx >= 0) rows[createdIdx] = stampCreated;
  else rows.push(stampCreated);
  return { ...mate, contextRows: rows };
}

function rosterForAggregateCard(
  card: LiffFinancialWorkCard,
): BacklogChildRow[] | undefined {
  const fromBacklog = normalizeBacklogChildren(card.backlogChildren);
  if ((fromBacklog?.length ?? 0) > 0) return fromBacklog;
  const items = card.outstanding?.items ?? [];
  if (items.length === 0) return undefined;
  return items.map((item) => ({
    objectRef: item.objectRef,
    label: item.label,
    status:
      item.status === "waiting_slip" || item.status === "paid"
        ? item.status
        : "open",
    remainingAmountMinor: item.remainingAmountMinor,
    paidAmountMinor: item.paidAmountMinor,
    originalAmountMinor: item.originalAmountMinor,
    occurredAt: item.occurredAt,
    expiresAt: item.expiresAt,
  }));
}

function batchIdForAggregateCard(card: LiffFinancialWorkCard): string {
  return (
    cardBatchId(card) ??
    normalizeBatchId(card.objectRef.id) ??
    card.objectRef.id
  );
}

function ensureBatchContextRows(
  card: LiffFinancialWorkCard,
  batchId: string,
  roster: BacklogChildRow[],
): LiffFinancialWorkCard {
  const rows = [...(card.contextRows ?? [])];
  const upsert = (key: string, label: string, value: string) => {
    const idx = rows.findIndex((row) => row.key === key);
    const next = { key, label, value };
    if (idx >= 0) rows[idx] = next;
    else rows.push(next);
  };
  const memberCount = Math.max(roster.length, cardBatchSize(card));
  const closedCount = roster.filter((row) => isBacklogChildPaid(row)).length;
  upsert("batch_id", "batch_id", batchId);
  upsert("batch_size", "batch_size", String(memberCount));
  upsert("batch_closed", "batch_closed", String(closedCount));
  return { ...card, contextRows: rows };
}

/** Desktop queue rows — expand batch aggregate cards into per-person receivable rows. */
export function expandReceivableBatchAggregateCards(
  cards: LiffFinancialWorkCard[],
): LiffFinancialWorkCard[] {
  if (cards.length === 0) return cards;

  const out: LiffFinancialWorkCard[] = [];
  for (const card of cards) {
    if (!isReceivableBatchCard(card)) {
      out.push(card);
      continue;
    }

    const roster = rosterForAggregateCard(card);
    const openChildren = (roster ?? []).filter((child) => !isBacklogChildPaid(child));
    if (openChildren.length === 0) {
      out.push(card);
      continue;
    }

    const batchId = batchIdForAggregateCard(card);
    const fullRoster = roster ?? openChildren;
    for (const child of openChildren) {
      const mate = batchMateCardFromChild(card, child, fullRoster);
      out.push(ensureBatchContextRows(mate, batchId, fullRoster));
    }
  }

  return clusterCardsByBatch(out);
}

/** Show every open batch mate on the queue — not only feed rows on the current page. */
export function expandBatchOpenMateCards(
  cards: LiffFinancialWorkCard[],
): LiffFinancialWorkCard[] {
  if (cards.length === 0) return cards;

  const byBatch = new Map<string, LiffFinancialWorkCard[]>();
  for (const card of cards) {
    if (!isSharedBatchCard(card)) continue;
    const batchId = cardBatchId(card);
    if (!batchId) continue;
    const list = byBatch.get(batchId) ?? [];
    list.push(card);
    byBatch.set(batchId, list);
  }

  const extras: LiffFinancialWorkCard[] = [];
  for (const group of byBatch.values()) {
    const template = group.find((card) => !isClosedWorkCard(card)) ?? group[0];
    if (!template || isClosedWorkCard(template)) continue;

    let roster: BacklogChildRow[] | undefined;
    for (const card of group) {
      roster = bestBatchRoster(roster, normalizeBacklogChildren(card.backlogChildren));
    }
    if ((roster?.length ?? 0) < 2) continue;

    const memberCount = Math.max(cardBatchSize(template), roster!.length);
    if (memberCount < 2) continue;

    const visibleIds = new Set(group.map((card) => card.objectRef.id));
    for (const child of roster!) {
      if (isBacklogChildPaid(child)) continue;
      if (visibleIds.has(child.objectRef.id)) continue;
      extras.push(batchMateCardFromChild(template, child, roster!));
      visibleIds.add(child.objectRef.id);
    }
  }

  if (extras.length === 0) return cards;
  return clusterCardsByBatch([...cards, ...extras]);
}

type QueueTabBucketId = "needs_action" | "waiting" | "recently_closed";

function cardQueueTabBucket(card: LiffFinancialWorkCard): QueueTabBucketId | null {
  const bucket = card.workPlacement?.bucket;
  if (
    bucket === "needs_action" ||
    bucket === "waiting" ||
    bucket === "recently_closed"
  ) {
    return bucket;
  }
  return null;
}

/**
 * Keep tab badges in sync when batch rows expand into individual queue cards.
 *
 * Only `needs_action` server counts treat a batch as one unit — expand may grow
 * that badge. `waiting` / `recently_closed` are already per-row (or closed totals);
 * mutating them from page expansion made badges flip across tabs (e.g. 2 ↔ 1).
 */
function reconcileTabCountsAfterExpansion(
  projection: WorkQueueProjection,
  beforePageCards: LiffFinancialWorkCard[],
  afterPageCards: LiffFinancialWorkCard[] | undefined,
): WorkQueueProjection {
  const nextCards = afterPageCards ?? [];
  const delta = nextCards.length - beforePageCards.length;
  if (delta === 0) return projection;

  const beforeIds = new Set(beforePageCards.map((card) => card.objectRef.id));
  const afterIds = new Set(nextCards.map((card) => card.objectRef.id));
  const removed = beforePageCards.filter((card) => !afterIds.has(card.objectRef.id));
  const added = nextCards.filter((card) => !beforeIds.has(card.objectRef.id));
  const tabCounts = projection.tabCounts
    ? { ...projection.tabCounts }
    : {
        needs_action: 0,
        waiting: 0,
        recently_closed: 0,
        all: 0,
      };

  const pageBucket = projection.page?.bucket;
  const replacedCluster = removed.length > 0;
  if (pageBucket === "needs_action") {
    if (replacedCluster || delta > 0) {
      tabCounts.needs_action = Math.max(
        0,
        (tabCounts.needs_action ?? 0) + delta,
      );
    }
  } else if (pageBucket === "all") {
    const removedNeeds = removed.filter(
      (card) => cardQueueTabBucket(card) === "needs_action",
    ).length;
    const addedNeeds = added.filter(
      (card) => cardQueueTabBucket(card) === "needs_action",
    ).length;
    if (replacedCluster) {
      tabCounts.needs_action = Math.max(
        0,
        (tabCounts.needs_action ?? 0) - removedNeeds + addedNeeds,
      );
    } else if (addedNeeds > 0) {
      tabCounts.needs_action = Math.max(
        0,
        (tabCounts.needs_action ?? 0) + addedNeeds,
      );
    }
  }
  tabCounts.all =
    tabCounts.needs_action + tabCounts.waiting + tabCounts.recently_closed;

  const page =
    projection.page != null
      ? {
          ...projection.page,
          total: Math.max(0, (projection.page.total ?? beforePageCards.length) + delta),
        }
      : projection.page;

  return {
    ...projection,
    tabCounts,
    page,
    buckets: projection.buckets.map((bucket) => {
      if (bucket.id === "needs_action" || bucket.id === "waiting" || bucket.id === "recently_closed") {
        return { ...bucket, count: tabCounts[bucket.id] };
      }
      return bucket;
    }),
  };
}

export function expandOpenBatchMatesInProjection(
  projection: WorkQueueProjection,
): WorkQueueProjection {
  const expand = (cards: LiffFinancialWorkCard[] | undefined) =>
    cards?.length
      ? expandBatchOpenMateCards(expandReceivableBatchAggregateCards(cards))
      : cards;
  const beforePageCards = projection.pageCards ?? [];
  const expanded = {
    ...projection,
    buckets: projection.buckets.map((bucket) => ({
      ...bucket,
      cards: expand(bucket.cards) ?? [],
    })),
    pageCards: expand(projection.pageCards),
  };
  return reconcileTabCountsAfterExpansion(
    expanded,
    beforePageCards,
    expanded.pageCards,
  );
}

export function mergeBatchRosterIntoCard(
  card: LiffFinancialWorkCard,
  queue: WorkQueueProjection | null | undefined,
): LiffFinancialWorkCard {
  if (!queue?.buckets?.length || queue.serverProjectionMissing) return card;
  let roster = normalizeBacklogChildren(card.backlogChildren);
  const fromQueue = queueRosterForCard(card, queue);
  if (fromQueue && rosterNeedsUpgrade(card, roster)) {
    roster = bestBatchRoster(roster, fromQueue);
  }
  if (!roster || roster === card.backlogChildren) return syncCardFromBatchRoster(card);
  return syncCardFromBatchRoster({ ...card, backlogChildren: roster });
}

function backlogChildrenForFeedItem(
  item: FinancialWorkFeedItem,
): BacklogChildRow[] | undefined {
  if ((item.batchMembers?.length ?? 0) >= 2) {
    return normalizeBacklogChildren(item.batchMembers);
  }
  return undefined;
}

/** Attach roster from same-page batch mates when the server omits batchMembers. */
export function attachFeedBatchChildren(
  cards: LiffFinancialWorkCard[],
  items: FinancialWorkFeedItem[],
): LiffFinancialWorkCard[] {
  const rosterByBatch = new Map<string, BacklogChildRow[]>();
  const byBatch = new Map<string, FinancialWorkFeedItem[]>();
  for (const item of items) {
    const batchId = normalizeBatchId(item.batchId);
    if (!batchId || (item.batchMemberCount ?? 0) < 2) continue;
    const serverRoster = normalizeBacklogChildren(item.batchMembers);
    if ((serverRoster?.length ?? 0) >= 2) {
      rosterByBatch.set(
        batchId,
        bestBatchRoster(rosterByBatch.get(batchId), serverRoster)!,
      );
    }
    const list = byBatch.get(batchId) ?? [];
    list.push(item);
    byBatch.set(batchId, list);
  }
  return cards.map((card) => {
    const batchId = cardBatchId(card);
    if (!batchId || cardBatchSize(card) < 2) return card;
    let roster = normalizeBacklogChildren(card.backlogChildren);
    const fromServer = lookupBatchRoster(rosterByBatch, batchId);
    if (fromServer) {
      roster = bestBatchRoster(roster, fromServer);
    }
    const mates = byBatch.get(batchId);
    if (mates && mates.length >= 2) {
      roster = bestBatchRoster(roster, mates.map(feedItemToBacklogChild));
    }
    if (!roster || (!rosterNeedsUpgrade(card, roster) && roster === card.backlogChildren)) {
      return syncCardFromBatchRoster(card);
    }
    return syncCardFromBatchRoster({ ...card, backlogChildren: roster });
  });
}

export function feedItemToCard(item: FinancialWorkFeedItem): LiffFinancialWorkCard {
  const nav = dataNavForItem(item);
  const options = item.clarifyOptions ?? [];
  const askKind =
    item.askKind && (HUMAN_ASK_KINDS as readonly string[]).includes(item.askKind)
      ? item.askKind
      : options.length >= 2
        ? ("slip_owner_select" as HumanAskKind)
        : null;
  const remainingMinor =
    item.remainingAmountMinor != null && item.remainingAmountMinor >= 0
      ? item.remainingAmountMinor
      : item.amountPaidMinor != null && item.amountPaidMinor > 0
        ? Math.max(item.amountMinor - item.amountPaidMinor, 0)
        : null;
  const paidMinor =
    item.amountPaidMinor != null && item.amountPaidMinor > 0
      ? item.amountPaidMinor
      : null;
  const isPartialReceivable =
    item.kind === "receivable" &&
    paidMinor != null &&
    remainingMinor != null &&
    remainingMinor > 0;
  const customerLabel = feedItemCustomerLabel(item);
  const displayTitle =
    isPartialReceivable
      ? batchMateTitle(customerLabel, remainingMinor)
      : item.title;
  const isAsk = Boolean(askKind);
  const surface =
    item.bucket === "recently_closed"
      ? "closed"
      : item.bucket === "waiting"
        ? "waiting"
        : "needs_action";
  const isClosedFeed = item.bucket === "recently_closed";
  const closedHints = isClosedFeed ? closedFeedCardHints(item) : null;
  const revision = 1;
  const backlogChildren = backlogChildrenForFeedItem(item);
  const reasonSubtitle = translateReasonSubtitle(item.subtitle);
  const comparisonNote = item.comparisonNote?.trim() || "";
  const requirementSummary =
    comparisonNote || reasonSubtitle || item.sourceLabel;
  return {
    objectRef: item.objectRef,
    projectionRevision: revision,
    audience: "scope_member",
    title: displayTitle,
    subtitle:
      closedHints?.matchLabel ?? (reasonSubtitle || item.sourceLabel),
    amountSummary: {
      currency: "THB",
      primaryAmountMinor: remainingMinor ?? item.amountMinor,
      basis: remainingMinor != null ? "remaining" : "cash",
    },
    outstanding:
      remainingMinor != null
        ? {
            openCount: 1,
            remainingAmountMinor: remainingMinor,
            currency: "THB",
            items: [
              {
                objectRef: item.objectRef,
                label: isPartialReceivable ? customerLabel : item.title,
                status: "waiting_slip",
                remainingAmountMinor: remainingMinor,
                paidAmountMinor: paidMinor ?? 0,
                originalAmountMinor: item.amountMinor,
                occurredAt: feedItemQueueAt(item),
                expiresAt: item.expiresAt?.trim() || undefined,
              },
            ],
          }
        : undefined,
    surfaceState: surface,
    workPlacement: { bucket: item.bucket, rank: 1 },
    requirementStatus: {
      complete: item.bucket === "recently_closed",
      summary: requirementSummary,
    },
    nextStep: {
      label: isAsk
        ? item.recommendation?.headline || "เลือกยอดที่สลิปนี้เป็นของ"
        : isPartialReceivable
          ? "จ่ายบางส่วน รอสลิปส่วนที่เหลือ"
          : reasonSubtitle || item.sourceLabel,
      assignedToCurrentActor: item.bucket === "needs_action",
    },
    contextRows: [
      { key: FEED_CONTEXT_KEY, label: "feed", value: "1" },
      {
        key: "source",
        label: "แหล่ง",
        value: closedHints?.sourceLabel ?? feedItemProvenanceSourceChannel(item),
      },
      { key: "kind", label: "ประเภท", value: item.kind },
      ...(item.incomeRecordId?.trim()
        ? [
            {
              key: "income_record",
              label: "รายรับ",
              value: item.incomeRecordId.trim(),
            },
          ]
        : []),
      ...(item.splitMode?.trim()
        ? [{ key: "split_mode", label: "โหมดแบ่งยอด", value: item.splitMode.trim() }]
        : []),
      ...(item.recommendation?.detail?.trim()
        ? [
            {
              key: "recommendation_detail",
              label: "แผนแนะนำ",
              value: item.recommendation.detail.trim(),
            },
          ]
        : []),
      ...(comparisonNote
        ? [
            {
              key: "comparison_note",
              label: "จุดต้องเทียบ",
              value: comparisonNote,
            },
          ]
        : []),
      ...(item.kind === "expense" && item.documentType?.trim()
        ? [
            {
              key: "document_type",
              label: "ประเภทเอกสาร",
              value: item.documentType.trim(),
            },
          ]
        : []),
      ...(item.imageUrl?.trim()
        ? [
            {
              key: "slip_image",
              label: "สลิป",
              value: item.imageUrl.trim(),
            },
          ]
        : []),
      ...(item.lastDecision
        ? [
            {
              key: "last_decision",
              label: "การตัดสินใจ",
              value: item.lastDecision,
            },
          ]
        : []),
      ...(closedHints?.closedByLabel
        ? [
            {
              key: "matched_receivable_customer",
              label: "ยอด",
              value: closedHints.closedByLabel,
            },
          ]
        : []),
      ...(closedHints?.matchMethod
        ? [
            {
              key: "match_method",
              label: "วิธีจับคู่",
              value: closedHints.matchMethod,
            },
          ]
        : []),
      {
        key: "created_at",
        label: "เวลาสร้าง",
        value: item.createdAt,
      },
      {
        key: "updated_at",
        label: "อัปเดตล่าสุด",
        value: item.updatedAt?.trim() || item.createdAt,
      },
      ...(item.expiresAt?.trim()
        ? [
            {
              key: "expires_at",
              label: "ครบกำหนด",
              value: item.expiresAt.trim(),
            },
          ]
        : []),
      ...(item.bucket === "recently_closed"
        ? [
            { key: "direction", label: "ทิศทาง", value: item.direction },
            {
              key: "occurred_at",
              label: "วันที่เกิดรายการ",
              value: feedItemQueueAt(item),
            },
            {
              key: "transfer_amount",
              label: "ยอดโอน",
              value: String(item.amountMinor),
            },
            ...(paidMinor != null
              ? [
                  {
                    key: "allocated_amount",
                    label: "จับคู่แล้ว",
                    value: String(paidMinor),
                  },
                ]
              : item.amountMinor > 0
                ? [
                    {
                      key: "allocated_amount",
                      label: "จับคู่แล้ว",
                      value: String(item.amountMinor),
                    },
                  ]
                : []),
            {
              key: "unallocated_amount",
              label: "รอจัดสรร",
              value: String(remainingMinor ?? 0),
            },
          ]
        : []),
      ...(isAsk && askKind
        ? [{ key: "ask_kind", label: "งาน", value: askKind }]
        : []),
      ...(item.batchId
        ? [
            { key: "batch_id", label: "batch_id", value: item.batchId },
            {
              key: "batch_size",
              label: "batch_size",
              value: String(item.batchMemberCount || 0),
            },
            {
              key: "batch_closed",
              label: "batch_closed",
              value: String(item.batchClosedCount || 0),
            },
          ]
        : []),
    ],
    consequenceRows: options.map((option) => {
      const confirmId = option.confirmId?.trim() || "";
      const receivableOrderId = (
        option.orderId ||
        option.receivableId ||
        ""
      ).replace(/^redis:/, "");
      const amountMinor =
        askKind === "slip_confirm" &&
        (option.amountMinor == null || option.amountMinor <= 0) &&
        item.amountMinor > 0
          ? item.amountMinor
          : option.amountMinor;
      let label = option.label;
      if (
        askKind === "slip_confirm" &&
        label &&
        /\(0(?:\.00)?\s*บาท\)/.test(label)
      ) {
        const party = option.partyName?.trim();
        label =
          party && party !== "ยืนยัน"
            ? label.replace(/\(0(?:\.00)?\s*บาท\)/, "").trim()
            : "ยอดนี้";
      }
      return {
        objectRef: confirmId
          ? { kind: "matched_pair" as const, id: confirmId }
          : receivableOrderId
            ? { kind: "receivable" as const, id: receivableOrderId }
            : { kind: "candidate" as const, id: option.candidateId },
        label,
        status: "open" as const,
        amountMinor,
        currency: "THB",
        occurredAt: option.createdAt?.trim() || undefined,
        expiresAt:
          option.expiresAt?.trim() ||
          (option.createdAt?.trim()
            ? (() => {
                const parsed = Date.parse(option.createdAt.trim());
                if (!Number.isFinite(parsed)) return undefined;
                return new Date(parsed + 14 * 24 * 60 * 60 * 1000).toISOString();
              })()
            : undefined),
      };
    }),
    evidenceSummary: { complete: 0, missing: 0, needsReview: 0 },
    timelineSummary: {
      latestEvent: item.subtitle || item.title,
      occurredAt: feedItemQueueAt(item),
    },
    timelineEvents: [
      {
        id: `tl-${item.objectRef.kind}-${item.objectRef.id}`,
        label: item.sourceLabel,
        occurredAtLabel: feedItemQueueAt(item),
      },
    ],
    availableActions:
      item.availableActions && item.availableActions.length > 0
        ? item.availableActions
        : isAsk
          ? []
          : [
              ...(item.bucket === "recently_closed"
                ? [
                    {
                      actionId: `correct-match-${item.objectRef.kind}-${item.objectRef.id}`,
                      actionType: "open_data",
                      objectRef: item.objectRef,
                      requiredRevision: revision,
                      requiredCapabilities: ["financial_work.read"],
                      riskTier: "low" as const,
                      requiredConfirmation: "none" as const,
                      operationTarget: "billlin.financial_work.correct_match",
                      operationKey: `fw:correct_match:${item.objectRef.kind}:${item.objectRef.id}:r${revision}`,
                      staleBehavior: "refresh" as const,
                      successProjectionRefs: [item.objectRef],
                      errorProjection: "refresh_card" as const,
                      label: "แก้การจับคู่",
                      dataNav: nav,
                    },
                  ]
                : []),
              {
                actionId: `open-data-${item.objectRef.kind}-${item.objectRef.id}`,
                actionType: "open_data",
                objectRef: item.objectRef,
                requiredRevision: revision,
                requiredCapabilities: ["financial_work.read"],
                riskTier: "low",
                requiredConfirmation: "none",
                operationTarget: "billlin.financial_work.open_data",
                operationKey: `fw:open_data:${item.objectRef.kind}:${item.objectRef.id}:r${revision}`,
                staleBehavior: "refresh",
                successProjectionRefs: [item.objectRef],
                errorProjection: "refresh_card",
                label: "ดูในข้อมูล",
                dataNav: nav,
              },
            ],
    detailLink: "",
    freshness: "current",
    ...(backlogChildren ? { backlogChildren } : {}),
  };
}

export function feedToQueueProjection(
  feed: FinancialWorkFeed,
  extras?: Pick<
    WorkQueueProjection,
    "canonicalTruth" | "navigation" | "actorCapabilities" | "actorLabel" | "scopeLabel"
  >,
): WorkQueueProjection {
  const byId = new Map(
    (feed.buckets || []).map((bucket) => [bucket.id, bucket]),
  );
  const visibleItems = (feed.items || []).filter((item) => !isJunkUnmatchedFeedItem(item));
  const dropped = (feed.items || []).length - visibleItems.length;
  const pageCards = attachFeedBatchChildren(
    sortWorkQueueCards(visibleItems.map(feedItemToCard)),
    visibleItems,
  );
  const tabCounts = feed.counts
    ? {
        ...feed.counts,
        needs_action: Math.max(0, (feed.counts.needs_action ?? 0) - (feed.bucket === "needs_action" ? dropped : 0)),
        all: Math.max(0, (feed.counts.all ?? 0) - dropped),
      }
    : feed.counts;
  const buckets = LIST_BUCKETS.map((id) => {
    const hit = byId.get(id);
    return {
      id,
      label: hit?.label || id,
      count: tabCounts?.[id] ?? hit?.count ?? 0,
      cards: id === feed.bucket ? pageCards : [],
    };
  });
  const pageSize = feed.pageSize ?? FEED_PAGE_SIZE;
  return expandOpenBatchMatesInProjection({
    projectionRevision: 1,
    freshness: "current",
    serverProjectionMissing: false,
    observedAt: feed.observedAt,
    actorLabel: extras?.actorLabel,
    scopeLabel: extras?.scopeLabel,
    actorCapabilities: extras?.actorCapabilities ?? feed.actorCapabilities,
    navigation: extras?.navigation ?? {
      sourcesAndSharing: { visible: false },
    },
    canonicalTruth: extras?.canonicalTruth,
    buckets,
    pageCards,
    tabCounts,
    page: {
      index: feed.page ?? 0,
      size: pageSize,
      total: Math.max(0, (feed.total ?? (feed.items || []).length) - dropped),
      bucket: feed.bucket || "all",
      q: feed.q || "",
    },
  });
}

const FEED_FETCH_PAGE_SIZE = 100;

/** Load every row from ทั้งหมด for overview totals (paginates until complete). */
export async function fetchAllWorkFeedItems(args: {
  workspaceId: string;
  ownerUserId?: string | null;
  actorId: string;
  fetchPage: (params: {
    workspaceId: string;
    ownerUserId?: string;
    actorId: string;
    bucket: string;
    q: string;
    page: number;
    pageSize: number;
  }) => Promise<FeedFetchOutcome>;
}): Promise<FinancialWorkFeedItem[]> {
  const ws = args.workspaceId.trim();
  if (!ws) return [];

  const seen = new Set<string>();
  const items: FinancialWorkFeedItem[] = [];
  let page = 0;
  let total = Number.POSITIVE_INFINITY;

  while (page * FEED_FETCH_PAGE_SIZE < total) {
    const outcome = await args.fetchPage({
      workspaceId: ws,
      ownerUserId: args.ownerUserId?.trim() || undefined,
      actorId: args.actorId,
      bucket: "all",
      q: "",
      page,
      pageSize: FEED_FETCH_PAGE_SIZE,
    });
    if (outcome.kind !== "ok") break;

    const pageItems = outcome.feed.items ?? [];
    for (const item of pageItems) {
      const ref = item.objectRef;
      const key = `${ref?.kind ?? ""}:${ref?.id ?? ""}`;
      if (!ref?.id || seen.has(key)) continue;
      seen.add(key);
      items.push(item);
    }

    total = outcome.feed.total ?? items.length;
    if (pageItems.length === 0) break;
    page += 1;
    if (page > 200) break;
  }

  return items;
}

export type { QueueErrorKind };
